/**
 * One- and two-sample z-tests for means when the population standard deviation is known.
 *
 * One sample: z = (x̄ − μ₀) / (σ / √n), with standard error σ/√n.
 *
 * Two independent samples: z = (x̄₁ − x̄₂ − Δ₀) / √(σ₁²/n₁ + σ₂²/n₂), with σ₁ and σ₂ the
 * known population SDs.
 *
 * The p-value comes from the standard normal distribution: two-sided 2·P(Z ≥ |z|), "less"
 * P(Z ≤ z), "greater" P(Z ≥ z). The confidence interval is two-sided at the chosen level,
 * estimate ± z* · SE with z* = Φ⁻¹(1 − (1 − level)/2). Cohen's d is the difference in
 * known-σ units: (x̄ − μ₀)/σ for one sample, and (x̄₁ − x̄₂)/√((σ₁² + σ₂²)/2) for two.
 *
 * If σ is estimated from the sample, the right test is the t-test; a z-test with the
 * sample SD is only an approximation that works for large n.
 */

import { mean, variance } from "@/lib/stats/descriptive";
import {
  normalCdf,
  normalQuantile,
  normalUpper,
} from "@/lib/stats/distributions";
import type { Alternative } from "@/lib/tool-kit/stats-tests/format";

export interface ZTestResult {
  kind: "one-sample" | "two-sample";
  /** x̄ for one sample, x̄₁ − x̄₂ for two. */
  estimate: number;
  /** μ₀ or Δ₀. */
  nullValue: number;
  se: number;
  z: number;
  alternative: Alternative;
  p: number;
  pTwoSided: number;
  pLess: number;
  pGreater: number;
  level: number;
  /** The two-sided critical value z* for the confidence level. */
  zCritical: number;
  ciLow: number;
  ciHigh: number;
  d: number;
  /** The critical value(s) for the rejection region at α, positive magnitudes. */
  criticalAlpha: number;
}

export type ZTestFailure = "invalid-n" | "invalid-sigma" | "invalid-input";

export type ZTestOutcome =
  | { ok: true; result: ZTestResult }
  | { ok: false; reason: ZTestFailure };

interface Options {
  alternative?: Alternative;
  level?: number;
  alpha?: number;
}

function finish(
  kind: ZTestResult["kind"],
  estimate: number,
  nullValue: number,
  se: number,
  sigmaForD: number,
  options: Options
): ZTestResult {
  const alternative = options.alternative ?? "two-sided";
  const level = options.level ?? 0.95;
  const alpha = options.alpha ?? 0.05;
  const z = (estimate - nullValue) / se;
  const pTwoSided = Math.min(1, 2 * normalUpper(Math.abs(z)));
  const pLess = normalCdf(z);
  const pGreater = normalUpper(z);
  const zCritical = normalQuantile(1 - (1 - level) / 2);
  return {
    kind,
    estimate,
    nullValue,
    se,
    z,
    alternative,
    p: { "two-sided": pTwoSided, less: pLess, greater: pGreater }[alternative],
    pTwoSided,
    pLess,
    pGreater,
    level,
    zCritical,
    ciLow: estimate - zCritical * se,
    ciHigh: estimate + zCritical * se,
    d: (estimate - nullValue) / sigmaForD,
    criticalAlpha: normalQuantile(
      alternative === "two-sided" ? 1 - alpha / 2 : 1 - alpha
    ),
  };
}

export function oneSampleZTest(
  input: { mean: number; n: number; sigma: number; mu0: number },
  options: Options = {}
): ZTestOutcome {
  const { mean: m, n, sigma, mu0 } = input;
  if (![m, n, sigma, mu0].every(Number.isFinite)) {
    return { ok: false, reason: "invalid-input" };
  }
  if (!Number.isInteger(n) || n < 1) {
    return { ok: false, reason: "invalid-n" };
  }
  if (!(sigma > 0)) {
    return { ok: false, reason: "invalid-sigma" };
  }
  return {
    ok: true,
    result: finish("one-sample", m, mu0, sigma / Math.sqrt(n), sigma, options),
  };
}

export function twoSampleZTest(
  input: {
    mean1: number;
    n1: number;
    sigma1: number;
    mean2: number;
    n2: number;
    sigma2: number;
    delta0: number;
  },
  options: Options = {}
): ZTestOutcome {
  const { mean1, n1, sigma1, mean2, n2, sigma2, delta0 } = input;
  if (![mean1, n1, sigma1, mean2, n2, sigma2, delta0].every(Number.isFinite)) {
    return { ok: false, reason: "invalid-input" };
  }
  if (!(Number.isInteger(n1) && Number.isInteger(n2)) || n1 < 1 || n2 < 1) {
    return { ok: false, reason: "invalid-n" };
  }
  if (!(sigma1 > 0 && sigma2 > 0)) {
    return { ok: false, reason: "invalid-sigma" };
  }
  const se = Math.sqrt((sigma1 * sigma1) / n1 + (sigma2 * sigma2) / n2);
  const pooled = Math.sqrt((sigma1 * sigma1 + sigma2 * sigma2) / 2);
  return {
    ok: true,
    result: finish("two-sample", mean1 - mean2, delta0, se, pooled, options),
  };
}

/** Summary statistics of raw data, for the "paste your data" option. */
export function summarizeSample(values: readonly number[]): {
  n: number;
  mean: number;
  sd: number;
} {
  return {
    n: values.length,
    mean: mean(values),
    sd: values.length > 1 ? Math.sqrt(variance(values)) : Number.NaN,
  };
}
